/**
 * `quoteBasket` — the ONE arithmetic behind every total a person reads (APP-ENTITLEMENT-001): the signup basket,
 * the API's basket quote, the Billing page's next invoice and the marketing worked example all call it, so no two
 * surfaces can disagree about a price.
 *
 * A basket is an anchor, the apps beside it and the quantity of each size dimension. The price book is the
 * published MONTHLY unit prices (rows the console entered and published), keyed by app and dimension; this module
 * never holds a price.
 *
 * @module @puffin-core/app-catalog/rules/quoteBasket
 */

'use strict';

const { getApp, isAnchorKey, attachesTo, appsAttachedTo } = require('../catalog');
const {
  PRICING_MODEL,
  BILLING_CYCLE,
  DIMENSION_KEY,
  QUANTITY_SOURCE,
  ANNUAL_MONTHS_CHARGED,
} = require('../vocabulary');
const { APP_CATALOG_REFUSAL, AppCatalogRefusal } = require('../refusal');
const { annualFromMonthly, monthlyEquivalentOfAnnual } = require('./pricing');

/**
 * @typedef {Object} Basket
 * @property {string} anchorAppKey
 * @property {string[]} appKeys - the apps beside the anchor (free apps may be omitted; they are always added).
 * @property {Object<string, Object<string, number>>} quantities - `{[appKey]: {[dimensionKey]: quantity}}`.
 * @property {string} [billingCycle='monthly']
 */

/**
 * @typedef {Object} PriceBook
 * @property {Object<string, Object<string, number>>} units - `{[appKey]: {[dimensionKey]: monthlyUnitAmountCents}}`.
 * @property {Iterable<string>} [offered] - the app keys the console currently OFFERS (`app_catalog."isOffered"`).
 *   When given, an app outside it is not sellable; the code's `isSellable` is the floor either way, so a row the
 *   code marks "coming soon" is never sold by a flag.
 */

function isOffered(entry, priceBook) {
  if (!entry.isSellable) return false;
  if (priceBook.offered === undefined) return true;
  return new Set(priceBook.offered).has(entry.appKey);
}

/** The published price of one dimension from an app's price map, or null while none is published. */
function publishedCentsOf(unitsOfApp, dimensionKey) {
  const cents = unitsOfApp[dimensionKey];
  return Number.isInteger(cents) && cents >= 0 ? cents : null;
}

function readPrice(unitsOfApp, appKey, dimensionKey) {
  const cents = publishedCentsOf(unitsOfApp, dimensionKey);
  if (cents === null) {
    throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.PRICE_NOT_PUBLISHED, { appKey, dimensionKey });
  }
  return cents;
}

function readQuantity(basket, appKey, dimension) {
  const forApp = basket.quantities[appKey] || {};
  const raw = forApp[dimension.dimensionKey];
  if (dimension.dimensionKey === DIMENSION_KEY.ACCOUNT) {
    // The account itself: exactly one, stated or not. A count here is a basket written against the wrong dimension.
    if (raw !== undefined && raw !== 1) {
      throw new TypeError(`quantity for ${appKey}.account is always 1, got ${JSON.stringify(raw)}`);
    }
    return 1;
  }
  if (raw === undefined) {
    // A dimension not stated takes what the anchor includes (three registers per location); a DERIVED one else takes
    // one unit (an account has a location, a line, a mailbox); a DECLARED one (the AI receptionist, Q-AE-27) is an
    // add-on the account chooses — none until it says so.
    if (dimension.includedPerParent) {
      const parentQuantity = readQuantity(basket, appKey, {
        dimensionKey: dimension.includedPerParent.dimensionKey,
        includedPerParent: null,
      });
      return parentQuantity * dimension.includedPerParent.quantity;
    }
    return dimension.quantitySource === QUANTITY_SOURCE.DECLARED ? 0 : 1;
  }
  if (!Number.isInteger(raw) || raw < 0) {
    throw new TypeError(
      `quantity for ${appKey}.${dimension.dimensionKey} must be a non-negative integer`,
    );
  }
  return raw;
}

function includedQuantityOf(basket, appKey, dimension) {
  if (!dimension.includedPerParent) return 0;
  const parentQuantity = readQuantity(basket, appKey, {
    dimensionKey: dimension.includedPerParent.dimensionKey,
    includedPerParent: null,
  });
  return parentQuantity * dimension.includedPerParent.quantity;
}

function perUnitLines(basket, priceBook, entry) {
  // An app the console has published no price for has no map at all; read it as an empty one, so a line nobody
  // buys is priced at nothing and a line somebody buys is refused by name.
  const unitsOfApp = priceBook.units[entry.appKey] || {};
  return entry.dimensions.map((dimension) => {
    const quantity = readQuantity(basket, entry.appKey, dimension);
    const includedQuantity = includedQuantityOf(basket, entry.appKey, dimension);
    if (dimension.includedPerParent && quantity < includedQuantity) {
      throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.QUANTITY_BELOW_INCLUDED, {
        appKey: entry.appKey,
        dimensionKey: dimension.dimensionKey,
        quantity,
        includedQuantity,
      });
    }
    const billableQuantity = Math.max(0, quantity - includedQuantity);
    // A line nobody buys (an add-on left at none) needs no published price: it costs nothing whatever the console has
    // entered; a line that is bought is priced only from a PUBLISHED price.
    const unitAmountCents =
      billableQuantity === 0
        ? (publishedCentsOf(unitsOfApp, dimension.dimensionKey) ?? 0)
        : readPrice(unitsOfApp, entry.appKey, dimension.dimensionKey);
    return {
      appKey: entry.appKey,
      pricingModel: entry.pricingModel,
      dimensionKey: dimension.dimensionKey,
      quantity,
      includedQuantity,
      billableQuantity,
      unitAmountCents,
      monthlyCents: billableQuantity * unitAmountCents,
      shareBps: null,
    };
  });
}

function entryLines(basket, priceBook, entry) {
  if (entry.pricingModel === PRICING_MODEL.PER_UNIT) return perUnitLines(basket, priceBook, entry);
  return [
    {
      appKey: entry.appKey,
      pricingModel: entry.pricingModel,
      dimensionKey: null,
      quantity: 1,
      includedQuantity: entry.pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR ? 1 : 0,
      billableQuantity: 0,
      unitAmountCents: 0,
      monthlyCents: 0,
      shareBps: entry.shareBps,
    },
  ];
}

function orderedAppKeys(basket, priceBook) {
  const anchor = getApp(basket.anchorAppKey);
  if (anchor === null || !isAnchorKey(basket.anchorAppKey)) {
    throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.ANCHOR_REQUIRED, {
      anchorAppKey: basket.anchorAppKey,
    });
  }
  if (!isOffered(anchor, priceBook)) {
    throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.APP_NOT_SELLABLE, { appKey: anchor.appKey });
  }
  const chosen = new Set(basket.appKeys);
  for (const appKey of chosen) {
    if (getApp(appKey) === null)
      throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.APP_UNKNOWN, { appKey });
    if (!attachesTo(appKey, anchor.appKey)) {
      throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.APP_NOT_ATTACHABLE, {
        appKey,
        anchorAppKey: anchor.appKey,
      });
    }
    if (!isOffered(getApp(appKey), priceBook)) {
      throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.APP_NOT_SELLABLE, { appKey });
    }
  }
  // Catalog order: the anchor, then every attached app that is free or chosen. Free apps are never optional — except
  // the SWITCHABLE one (customer accounts, Q-LS-85): a per-company setting rides the anchor at creation (the
  // provisioning rule adds it there) and is never a line anyone reads on a quote, so it is quoted only when named.
  return [
    anchor.appKey,
    ...appsAttachedTo(anchor.appKey)
      .filter(
        (entry) =>
          (entry.pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR && !entry.switchable) ||
          chosen.has(entry.appKey),
      )
      .map((entry) => entry.appKey),
  ];
}

/**
 * Quote a basket against the published price book.
 *
 * @param {{basket: Basket, priceBook: PriceBook}} params
 * @returns {{
 *   anchorAppKey: string, billingCycle: string, appKeys: string[], freeAppKeys: string[],
 *   lines: Object[], monthlyCents: number, annualCents: number, annualMonthlyEquivalentCents: number,
 *   monthsChargedOnAnnual: number, dueTodayCents: number
 * }} `dueTodayCents` is the cycle's charge once the trials convert — the monthly total or the annual total.
 * @throws {AppCatalogRefusal} `ANCHOR_REQUIRED`, `APP_NOT_SELLABLE` (coming soon in code, or not offered by the
 *   console), `APP_UNKNOWN`, `APP_NOT_ATTACHABLE`, `QUANTITY_BELOW_INCLUDED`, `PRICE_NOT_PUBLISHED`.
 */
function quoteBasket({ basket, priceBook }) {
  const billingCycle =
    basket.billingCycle === undefined ? BILLING_CYCLE.MONTHLY : basket.billingCycle;
  if (billingCycle !== BILLING_CYCLE.MONTHLY && billingCycle !== BILLING_CYCLE.ANNUAL) {
    throw new TypeError(
      `billingCycle must be monthly or annual, got ${JSON.stringify(billingCycle)}`,
    );
  }
  const appKeys = orderedAppKeys(basket, priceBook);
  const lines = appKeys.flatMap((appKey) => entryLines(basket, priceBook, getApp(appKey)));
  const monthlyCents = lines.reduce((sum, line) => sum + line.monthlyCents, 0);
  const annualCents = annualFromMonthly(monthlyCents);
  return {
    anchorAppKey: basket.anchorAppKey,
    billingCycle,
    appKeys,
    freeAppKeys: appKeys.filter(
      (appKey) => getApp(appKey).pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR,
    ),
    lines,
    monthlyCents,
    annualCents,
    annualMonthlyEquivalentCents: monthlyEquivalentOfAnnual(annualCents),
    monthsChargedOnAnnual: ANNUAL_MONTHS_CHARGED,
    dueTodayCents: billingCycle === BILLING_CYCLE.ANNUAL ? annualCents : monthlyCents,
  };
}

module.exports = { quoteBasket };
